import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Alert, Button, Container, HeaderSection } from '@groupelacentrale/contact-storybook';
import { PublicationOptionInfos } from './PublicationOptionInfos';
import { PublicationOption } from './PublicationOption';
import { PublicationOptionSecuritel } from './PublicationOptionSecuritel';
import { StrengthsModal } from './StrengthsModal';
import { PayboxTypeCarte, PublicationOption as PublicationOptionType } from 'types';
import { keypressCallback } from 'utils';
import { PaymentCardSelect } from 'components';
import './PublicationOption.scss';

interface Props {
  publicationOptions: PublicationOptionType[];
  strengths: Array<{ label: string; iconUrl: string }>;
  phone?: string;
  values: {
    productIds: string[];
    cgu: boolean;
    totalPrice: number;
    pbxTypeCarte: PayboxTypeCarte;
  };
  errorMessage?: string;
  isSubmitting: boolean;
  errors: { cgu?: string; pbxTypeCarte?: string };
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => any;
  onChange: (e: React.ChangeEvent) => void | undefined;
  onReturn: () => void | undefined;
  onSubmit: () => undefined | void;
  isRenewal?: boolean;
}

export const PublicationOptionFormView: React.FC<Props> = ({
  publicationOptions,
  strengths,
  phone,
  values,
  errorMessage,
  isSubmitting,
  errors,
  setFieldValue,
  onChange,
  onReturn,
  onSubmit,
  isRenewal,
}) => {
  const [options, setOptions] = useState(publicationOptions);

  const formRef = useRef<HTMLFormElement | null>(null);

  // Custom text for points_forts
  const optionText = useCallback(
    (option: PublicationOptionType) =>
      option.name === 'points_forts' ? (
        <>
          Les principaux points forts sont affichés sur votre annonce pendant 30 jours.
          <br />
          <StrengthsModal strengths={strengths} />
        </>
      ) : (
        option.text
      ),
    [strengths],
  );

  // Custom picture for securitel
  const customPicture = useCallback(
    (option: PublicationOptionType) =>
      option.name === 'securitel' ? <PublicationOptionSecuritel number={phone || '0633445566'} /> : undefined,
    [phone],
  );

  const checkUncheckOption = (newOptions: PublicationOptionType[], optionIndex: number) => {
    // Add / remove option
    newOptions[optionIndex].checked = !newOptions[optionIndex].checked;

    // Uncheck related offers
    // else Enable related offers
    if (newOptions[optionIndex].checked) {
      for (const relatedOfferIndex in newOptions[optionIndex].relatedOffers) {
        newOptions[relatedOfferIndex].checked = true;
        newOptions[relatedOfferIndex].active = false;
      }
    } else {
      for (const relatedOfferIndex in newOptions[optionIndex].relatedOffers) {
        newOptions[relatedOfferIndex].checked = false;
        newOptions[relatedOfferIndex].active = true;
      }
    }
  };

  const handleOptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const optionIndex = options.findIndex(o => o.name === e.currentTarget.name);

      if (optionIndex >= 0) {
        const newOptions = options;

        checkUncheckOption(newOptions, optionIndex);

        const checkedOptions = newOptions.filter(co => co.checked).map(co => co.id);

        // Check packed offer if there is one
        for (const otherOptionIndex in options) {
          if (optionIndex !== Number(otherOptionIndex)) {
            const relatedOffers = options[otherOptionIndex].relatedOffers;
            if (
              relatedOffers.length > 0 &&
              relatedOffers.every(ro => checkedOptions.includes(ro)) &&
              relatedOffers.includes(options[optionIndex].id)
            ) {
              checkUncheckOption(newOptions, Number(otherOptionIndex));
            }
          }
        }

        setOptions([...newOptions]);
      }
    },
    [options],
  );

  const totalPrice = useMemo(() => {
    return options.reduce(
      (total, currentValue) => total + (currentValue.checked && currentValue.active ? currentValue.price : 0),
      0,
    );
  }, [options]);

  const handleSubmit = useCallback(() => {
    setFieldValue('totalPrice', totalPrice);
    setFieldValue(
      'productIds',
      options.filter(o => o.checked && o.active).map(o => o.id),
    );
    onSubmit();

    //eslint-disable-next-line @typescript-eslint/no-unused-expressions
    formRef.current?.submit();
  }, [totalPrice, options, setFieldValue, onSubmit]);

  const handleKeyPress = keypressCallback(
    (option: keyof typeof values) => setFieldValue(option, !values[option]),
    'data-option',
  );

  const labelWording = isRenewal ? 'Renouveler' : 'Déposer';

  return (
    <Container
      shadowed
      paddings="mediumP"
      backgroundColor="bgWhite"
      className={`publication-option-form options-${publicationOptions.length}`}
    >
      <HeaderSection noUnderline>Valorisez votre annonce !</HeaderSection>
      <div className="publication-options">
        {options.map(option => (
          <PublicationOption
            key={option.name}
            name={option.name}
            title={option.title ?? option.label}
            picture={option.picture}
            picture2={option.picture2}
            price={option.price}
            text={optionText(option)}
            estimatedPrice={option.estimatedPrice}
            customPicture={customPicture(option)}
            checked={option.checked}
            onCheck={handleOptionChange}
            disabled={!option.active}
            onKeyPressCheck={handleKeyPress}
          />
        ))}
      </div>
      <PublicationOptionInfos
        checked={values.cgu}
        onChange={onChange}
        name="cgu"
        error={errors.cgu}
        onKeyPressCheck={handleKeyPress}
      />
      {errorMessage && <Alert type="error">{errorMessage}</Alert>}
      {totalPrice > 0 && (
        <PaymentCardSelect
          name="pbxTypeCarte"
          value={values.pbxTypeCarte}
          onChange={onChange}
          error={errors.pbxTypeCarte}
        />
      )}
      <div className="actions">
        <Button label="Retour" theme="quaternary" type="button" onClick={onReturn} />
        <Button
          disabled={isSubmitting || (totalPrice > 0 && values.pbxTypeCarte === undefined) || !values.cgu}
          loading={isSubmitting}
          label={totalPrice > 0 ? `${labelWording} et payer ${totalPrice} €` : `${labelWording} gratuitement`}
          type="button"
          onClick={handleSubmit}
        />
      </div>
    </Container>
  );
};
